import { type Editor, useEditorState } from '@tiptap/react';
import { CornerDownLeft, ExternalLink, Link2, Trash } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@avanta/shared/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@avanta/shared/components/ui/Popover';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { CHAT_BUTTON_CLASSNAME, CHAT_BUTTON_INPUT_CLASSNAME } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { openInNewTab } from '@avanta/shared/utils/common';

interface LinkButtonProps {
  editor: Editor;
}

export const LinkButton = ({ editor }: LinkButtonProps): React.ReactElement => {
  const { currentHref, hasSelection, isActive } = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      currentHref: (e.getAttributes('link').href as string | undefined) ?? '',
      hasSelection: !e.state.selection.empty,
      isActive: e.isActive('link'),
    }),
  });
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setUrl(open ? currentHref : '');
    },
    [currentHref]
  );

  const applyLink = useCallback(() => {
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank' }).run();
    }
  }, [url, editor]);

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        applyLink();
      }
    },
    [applyLink]
  );

  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          icon="only"
          aria-label={t('Chat.RichTextInput.link')}
          aria-pressed={isActive}
          disabled={!hasSelection}
          className={cn(
            CHAT_BUTTON_INPUT_CLASSNAME,
            isActive && '[&>svg]:stroke-3',
            !hasSelection && 'opacity-50 [&>svg]:stroke-2'
          )}
        >
          <Link2 className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="flex items-center gap-0.5 p-1.5 min-w-56 w-auto"
        aria-label={t('Chat.RichTextInput.linkDialog')}
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <input
          ref={inputRef}
          type="url"
          value={url}
          onChange={event => setUrl(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://..."
          className={cn(
            'flex-1 text-xs bg-transparent outline-none',
            'placeholder:text-gray-400 text-gray-800',
            'min-w-0'
          )}
        />
        <Button
          type="button"
          variant="ghost"
          icon="only"
          aria-label={t('Chat.RichTextInput.applyLink')}
          className={cn(CHAT_BUTTON_CLASSNAME, 'hover:[&>svg]:stroke-teal-600')}
          onClick={applyLink}
        >
          <CornerDownLeft className="size-3.5" />
        </Button>
        {isActive && (
          <>
            <Separator orientation="vertical" className="data-[orientation=vertical]:h-6" />
            <Button
              type="button"
              variant="ghost"
              icon="only"
              aria-label={t('Chat.RichTextInput.openLink')}
              className={CHAT_BUTTON_CLASSNAME}
              onClick={() => openInNewTab(url)}
            >
              <ExternalLink className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              icon="only"
              aria-label={t('Chat.RichTextInput.removeLink')}
              className={CHAT_BUTTON_CLASSNAME}
              onClick={removeLink}
            >
              <Trash className="size-3.5" />
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
